import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { parseApiError } from 'utils/errorParser';
import { notify } from 'features/ui/uiSlice';
import { MESSAGES } from 'constants/messages';

/**
 * Factory that builds a standard CRUD slice + thunks for an entity backed by a
 * service exposing { list, create, update, remove }. Keeps the many
 * settings/list modules DRY while preserving the
 * Component → Thunk → Service → Axios contract.
 *
 * service.list() must return { items, total, page, pageSize, totalPages }.
 *
 * Returns: { reducer, thunks: { fetch, create, update, remove }, actions }
 */
export const createCrudSlice = ({ name, service, idKey = 'id' }) => {
  const fetch = createAsyncThunk(`${name}/fetch`, async (params, { rejectWithValue }) => {
    try {
      return await service.list(params);
    } catch (error) {
      return rejectWithValue(parseApiError(error));
    }
  });

  const create = createAsyncThunk(`${name}/create`, async (model, { dispatch, rejectWithValue }) => {
    try {
      const created = await service.create(model);
      dispatch(notify({ type: 'success', message: MESSAGES.CRUD.CREATE_SUCCESS }));
      return created;
    } catch (error) {
      const parsed = parseApiError(error);
      dispatch(notify({ type: 'error', message: parsed.message }));
      return rejectWithValue(parsed);
    }
  });

  const update = createAsyncThunk(`${name}/update`, async ({ id, model }, { dispatch, rejectWithValue }) => {
    try {
      const updated = await service.update(id, model);
      dispatch(notify({ type: 'success', message: MESSAGES.CRUD.UPDATE_SUCCESS }));
      return updated;
    } catch (error) {
      const parsed = parseApiError(error);
      dispatch(notify({ type: 'error', message: parsed.message }));
      return rejectWithValue(parsed);
    }
  });

  const remove = createAsyncThunk(`${name}/remove`, async (id, { dispatch, rejectWithValue }) => {
    try {
      await service.remove(id);
      dispatch(notify({ type: 'success', message: MESSAGES.CRUD.DELETE_SUCCESS }));
      return id;
    } catch (error) {
      const parsed = parseApiError(error);
      dispatch(notify({ type: 'error', message: parsed.message }));
      return rejectWithValue(parsed);
    }
  });

  const slice = createSlice({
    name,
    initialState: {
      items: [],
      meta: { total: 0, page: 1, pageSize: 20, totalPages: 0 },
      status: 'idle',
      saving: false,
      error: null,
    },
    reducers: {
      upsertOne(state, action) {
        const idx = state.items.findIndex((i) => i[idKey] === action.payload[idKey]);
        if (idx === -1) state.items.unshift(action.payload);
        else state.items[idx] = action.payload;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetch.pending, (state) => {
          state.status = 'loading';
          state.error = null;
        })
        .addCase(fetch.fulfilled, (state, action) => {
          state.status = 'succeeded';
          state.items = action.payload.items;
          state.meta = {
            total: action.payload.total,
            page: action.payload.page,
            pageSize: action.payload.pageSize,
            totalPages: action.payload.totalPages,
          };
        })
        .addCase(fetch.rejected, (state, action) => {
          state.status = 'failed';
          state.error = action.payload?.message || 'Failed to load';
        })
        .addCase(create.pending, (state) => { state.saving = true; })
        .addCase(create.fulfilled, (state, action) => {
          state.saving = false;
          state.items.unshift(action.payload);
          state.meta.total += 1;
        })
        .addCase(create.rejected, (state) => { state.saving = false; })
        .addCase(update.pending, (state) => { state.saving = true; })
        .addCase(update.fulfilled, (state, action) => {
          state.saving = false;
          const idx = state.items.findIndex((i) => i[idKey] === action.payload[idKey]);
          if (idx !== -1) state.items[idx] = action.payload;
        })
        .addCase(update.rejected, (state) => { state.saving = false; })
        .addCase(remove.fulfilled, (state, action) => {
          state.items = state.items.filter((i) => i[idKey] !== action.payload);
          if (state.meta.total > 0) state.meta.total -= 1;
        });
    },
  });

  return {
    reducer: slice.reducer,
    actions: slice.actions,
    thunks: { fetch, create, update, remove },
  };
};

export default createCrudSlice;
